import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import CopyIconSvg from '../../../assets/icons/icon-home-dashboard-copy.svg?react';
import ShareIconSvg from '../../../assets/icons/icon-home-dashboard-share.svg?react';
import { useLinkShareActions } from '../../../shared/hooks/use-link-share-actions';
import { BaseButton } from '../../../shared/ui';
import { useTipsShareQrUrl } from '../../tips-share/model/use-tips-share-qr-url';
import { TipsShareQrCode } from '../../tips-share/ui/tips-share-qr-code';

const COPY_TOAST_DURATION_MS = 2200;

const copyToastLifecycle = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, 6px);
  }

  12% {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  88% {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, 6px);
  }
`;

type HomeDashboardSharePanelProps = {
  onOpenQrFullscreen: () => void;
};

const Panel = styled.div.attrs({
  'data-ui': 'home-dashboard-share-panel',
})`
  position: relative;
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(7)};
`;

const ActionsRow = styled.div`
  width: ${({ theme }) => theme.scale.px(157)};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  justify-content: center;
`;

const ActionButton = styled(BaseButton)`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(5)};
`;

const ActionIconCircle = styled.span`
  width: ${({ theme }) => theme.scale.px(29)};
  height: ${({ theme }) => theme.scale.px(29)};
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.support.infoSubtle};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionLabel = styled.span`
  width: ${({ theme }) => theme.scale.px(60)};
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(7)};
  line-height: ${({ theme }) => theme.scale.px(8)};
  white-space: pre-line;
`;

const ShareIcon = styled(ShareIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(11)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const CopyIcon = styled(CopyIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(13)};
  height: ${({ theme }) => theme.scale.px(15)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const QrCode = styled(TipsShareQrCode).attrs({
  size: 99,
})`
  border-radius: ${({ theme }) => theme.scale.px(8)};
`;

const Hint = styled.p`
  margin: 0;
  width: ${({ theme }) => theme.scale.px(136)};
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(7)};
  line-height: ${({ theme }) => theme.scale.px(9)};
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.5;
`;

const QrSlot = styled.div`
  display: grid;
  place-items: center;
`;

const QrOpenButton = styled(BaseButton).attrs({
  'data-ui': 'home-dashboard-open-qr-button',
  type: 'button',
})`
  display: grid;
  place-items: center;
`;

const CopyToast = styled.div.attrs({
  'aria-live': 'polite',
  'data-ui': 'home-dashboard-copy-toast',
  role: 'status',
})`
  min-width: ${({ theme }) => theme.scale.px(108)};
  max-width: ${({ theme }) => theme.scale.px(160)};
  box-sizing: border-box;
  position: absolute;
  left: 50%;
  bottom: ${({ theme }) => theme.scale.px(12)};
  transform: translate(-50%, 0);
  padding: ${({ theme }) => theme.scale.px(8)}
    ${({ theme }) => theme.scale.px(12)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.surface.base};
  font-size: ${({ theme }) => theme.scale.px(8)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(10)};
  text-align: center;
  box-shadow: 0 ${({ theme }) => theme.scale.px(8)}
    ${({ theme }) => theme.scale.px(24)} rgba(27, 27, 27, 0.18);
  pointer-events: none;
  animation: ${copyToastLifecycle} ${COPY_TOAST_DURATION_MS}ms ease both;
`;

export function HomeDashboardSharePanel({
  onOpenQrFullscreen,
}: HomeDashboardSharePanelProps) {
  const { t } = useTranslation();
  const tipsShareLink = useTipsShareQrUrl();
  const { copyLink: copyTipsShareLink, shareLink: shareTipsShareLink } =
    useLinkShareActions(tipsShareLink);
  const copyToastTimerRef = useRef<number | null>(null);
  const [copyToastKey, setCopyToastKey] = useState(0);
  const [isCopyToastVisible, setIsCopyToastVisible] = useState(false);

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current !== null) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  const showCopyToast = () => {
    setIsCopyToastVisible(true);
    setCopyToastKey(currentKey => currentKey + 1);

    if (copyToastTimerRef.current !== null) {
      window.clearTimeout(copyToastTimerRef.current);
    }

    copyToastTimerRef.current = window.setTimeout(() => {
      setIsCopyToastVisible(false);
      copyToastTimerRef.current = null;
    }, COPY_TOAST_DURATION_MS);
  };

  const handleCopyLink = async () => {
    const isCopied = await copyTipsShareLink();

    if (!isCopied) {
      return;
    }

    showCopyToast();
  };

  const handleShareLink = async () => {
    const result = await shareTipsShareLink();

    if (result === 'copied') {
      showCopyToast();
    }
  };

  return (
    <Panel>
      <ActionsRow>
        <ActionButton
          type="button"
          onClick={() => {
            void handleShareLink();
          }}
        >
          <ActionIconCircle>
            <ShareIcon />
          </ActionIconCircle>
          <ActionLabel>{t('homeDashboard.shareAction')}</ActionLabel>
        </ActionButton>
        <ActionButton
          type="button"
          onClick={() => {
            void handleCopyLink();
          }}
        >
          <ActionIconCircle>
            <CopyIcon />
          </ActionIconCircle>
          <ActionLabel>{t('homeDashboard.copyAction')}</ActionLabel>
        </ActionButton>
      </ActionsRow>
      <QrSlot>
        <QrOpenButton
          aria-label={t('homeDashboard.qrHint')}
          title={t('homeDashboard.qrHint')}
          onClick={onOpenQrFullscreen}
        >
          <QrCode tipsShareUrl={tipsShareLink} />
        </QrOpenButton>
      </QrSlot>
      <Hint>{t('homeDashboard.qrHint')}</Hint>
      {isCopyToastVisible ? (
        <CopyToast key={copyToastKey}>
          {t('homeDashboard.copySuccessToast')}
        </CopyToast>
      ) : null}
    </Panel>
  );
}
